'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';

export type PresenceStatus = 'online' | 'away' | 'in_a_meeting' | 'offline';

export interface StaffPresence {
  userId: string;
  status: PresenceStatus;
  statusNote?: string;
  updatedAt: string;
}

export function usePresence(currentUserId?: string) {
  const [presenceMap, setPresenceMap] = useState<Record<string, StaffPresence>>({});
  const [loading, setLoading] = useState(true);
  const supabase = useMemo(() => createClient(), []);

  const fetchAll = useCallback(async () => {
    const { data } = await supabase
      .from('staff_presence')
      .select('user_id, status, status_note, updated_at');
    if (data) {
      const map: Record<string, StaffPresence> = {};
      data.forEach((row: any) => {
        map[row.user_id] = {
          userId: row.user_id,
          status: row.status as PresenceStatus,
          statusNote: row.status_note ?? undefined,
          updatedAt: row.updated_at,
        };
      });
      setPresenceMap(map);
    }
    setLoading(false);
  }, [supabase]);

  const setMyStatus = useCallback(
    async (status: PresenceStatus, statusNote?: string) => {
      if (!currentUserId) return;
      await supabase.from('staff_presence').upsert(
        { user_id: currentUserId, status, status_note: statusNote ?? null, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      );
    },
    [currentUserId, supabase]
  );

  useEffect(() => {
    fetchAll();

    const channel = supabase
      .channel('presence-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'staff_presence' },
        (payload) => {
          const row = payload.new as any;
          if (!row?.user_id) return;
          setPresenceMap((prev) => ({
            ...prev,
            [row.user_id]: {
              userId: row.user_id,
              status: row.status as PresenceStatus,
              statusNote: row.status_note ?? undefined,
              updatedAt: row.updated_at,
            },
          }));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAll, supabase]);

  const getStatus = useCallback(
    (userId: string): PresenceStatus => presenceMap[userId]?.status ?? 'offline',
    [presenceMap]
  );

  return { presenceMap, loading, setMyStatus, getStatus };
}
